import {
  OutlinedSelect,
  Button,
  OutlinedSelectOptionType,
  useMobile,
} from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';

import { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SearchParams } from '../types';
import { getClassesBookForm } from '../api';
import { HandleSearch } from './list';

type FormFields = SearchParams<OutlinedSelectOptionType | null>;

const defaultValues: FormFields = {
  period: null,
  school: null, // facultad
  campus: null, // sede
  section: null,
  course: null,
};

interface SearchControlsProps {
  onSearch?: ({ searchParams, page }: HandleSearch) => void;
  onClear?: () => void;
  onFilter?: (params?: SearchParams<string>) => void;
}

const SearchControls = ({
  onSearch,
  onClear,
  onFilter,
}: SearchControlsProps) => {
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_LIST);
  const isMobile = useMobile();
  const { control, handleSubmit, reset } = useForm<FormFields>({
    defaultValues,
  });

  const [isMounted, setIsMounted] = useState<boolean>();

  const [periods, setPeriods] = useState<OutlinedSelectOptionType[]>([]);
  const [schools, setSchools] = useState<OutlinedSelectOptionType[]>([]);
  const [campuses, setCampuses] = useState<OutlinedSelectOptionType[]>([]);
  const [courses, setCourses] = useState<OutlinedSelectOptionType[]>([]);
  const [sections, setSections] = useState<OutlinedSelectOptionType[]>([]);

  const handleSearch = useCallback(
    (values: FormFields) => {
      const searchParams = {
        period: values?.period?.value?.toString() ?? undefined,
        school: values?.school?.value?.toString() ?? undefined,
        campus: values?.campus?.value?.toString() ?? undefined,
        course: values?.course?.value?.toString() ?? undefined,
        section: values?.section?.value?.toString() ?? undefined,
        hasFilters: Object.values(values).some((value) => value !== null),
      };

      !!onFilter && onFilter({ ...searchParams });
      !!onSearch && onSearch({ searchParams, page: 1 });
    },
    [onFilter, onSearch],
  );

  const handleClear = useCallback(() => {
    reset(defaultValues);
    !!onClear && onClear();
    !!onFilter && onFilter(undefined);
    !!onSearch &&
      onSearch({
        searchParams: {},
        page: 1,
      });
  }, [reset, onClear, onFilter, onSearch]);

  useEffect(() => {
    if (!isMounted) {
      getClassesBookForm().then(({ data }) => {
        setPeriods(data?.periods ?? []);
        setSchools(data?.schools ?? []);
        setCampuses(data?.campuses ?? []);
        setCourses(data?.courses ?? []);
        setSections(data?.sections ?? []);
      });
      setIsMounted(true);
    }
  }, [isMounted]);

  return (
    <Row>
      <Col xs={12} sm={6} md={3}>
        <OutlinedSelect
          label={t(`searchControls.period`)}
          name="period"
          control={control}
          options={periods}
          disabled={!periods?.length}
        />
      </Col>
      <Col xs={12} sm={6} md={3}>
        <OutlinedSelect
          label={t(`searchControls.school`)}
          name="school"
          control={control}
          options={schools}
          disabled={!schools?.length}
        />
      </Col>
      <Col xs={12} sm={6} md={3}>
        <OutlinedSelect
          label={t(`searchControls.campus`)}
          name="campus"
          control={control}
          options={campuses}
          disabled={!campuses?.length}
        />
      </Col>
      <Col xs={12} sm={6} md={3}>
        <OutlinedSelect
          label={t(`searchControls.course`)}
          name="course"
          control={control}
          options={courses}
          disabled={!courses?.length}
        />
      </Col>
      <Col xs={12} sm={6} md={3}>
        <OutlinedSelect
          label={t(`searchControls.section`)}
          name="section"
          control={control}
          options={sections}
          disabled={!sections?.length}
        />
      </Col>
      <Col
        xs={12}
        md={{ size: 6, offset: 3 }}
        lg={{ size: 3, offset: 6 }}
        style={{ marginTop: isMobile ? 0 : '1.5rem' }}
      >
        <div
          className="d-flex w-100 align-items-center h-100"
          style={{ gap: 8 }}
        >
          <Button
            text={t(`searchControls.search`)}
            size="md"
            type="button"
            className="flex-fill"
            onClick={handleSubmit(handleSearch)}
          />
          <Button
            text={t(`searchControls.clear`)}
            size="md"
            type="button"
            className="flex-fill"
            outlined
            onClick={handleClear}
          />
        </div>
      </Col>
    </Row>
  );
};

export default SearchControls;
